import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Document,
  DocumentKind,
  ExtractionPreview,
  Topic,
} from '@anchor-comms/shared';
import { api, apiBase } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

const KIND_LABELS: Record<DocumentKind, string> = {
  brand_voice: 'Brand voice',
  knowledge_base: 'Knowledge base',
  example_thread: 'Example thread',
};

export function DocumentsPage() {
  const qc = useQueryClient();
  const docsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => api<Document[]>('/api/v1/documents', { query: { includeInactive: true } }),
  });
  const topicsQuery = useQuery({
    queryKey: ['topics'],
    queryFn: () => api<Topic[]>('/api/v1/topics'),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Knowledge</h1>

      <Uploader
        topics={topicsQuery.data ?? []}
        onCreated={() => qc.invalidateQueries({ queryKey: ['documents'] })}
      />

      <PastedTextCreate
        topics={topicsQuery.data ?? []}
        onCreated={() => qc.invalidateQueries({ queryKey: ['documents'] })}
      />

      <div className="space-y-2">
        <h2 className="text-lg font-medium">Documents</h2>
        {docsQuery.data?.map((d) => (
          <Card key={d.id} className="p-4">
            <div className="flex justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{d.name}</span>
                <Badge variant="outline">{KIND_LABELS[d.kind]}</Badge>
                {d.alwaysInclude && <Badge>always included</Badge>}
                {!d.isActive && <Badge variant="destructive">inactive</Badge>}
              </div>
              <span className="text-xs text-muted-foreground">{d.content.length} chars</span>
            </div>
            {d.originalFilename && (
              <p className="text-xs text-muted-foreground mt-1">From file: {d.originalFilename}</p>
            )}
          </Card>
        ))}
        {docsQuery.data && docsQuery.data.length === 0 && (
          <p className="text-muted-foreground">No documents yet. Upload a file or paste text above.</p>
        )}
      </div>
    </div>
  );
}

function Uploader({ topics, onCreated }: { topics: Topic[]; onCreated: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ExtractionPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<DocumentKind>('knowledge_base');
  const [topicId, setTopicId] = useState<string>('');
  const [alwaysInclude, setAlwaysInclude] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPreview = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setError(null);
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${apiBase}/api/v1/documents/upload-preview`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message ?? 'Upload failed');
      }
      const data = (await res.json()) as ExtractionPreview;
      setPreview(data);
      setName(file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  const confirm = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('No preview');
      return api<Document>('/api/v1/documents/from-upload', {
        method: 'POST',
        body: {
          name,
          kind,
          topicId: topicId || null,
          alwaysInclude,
          uploadKey: preview.uploadKey,
        },
      });
    },
    onSuccess: () => {
      setPreview(null);
      setName('');
      onCreated();
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload PDF or DOCX</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-center">
          <Input type="file" accept=".pdf,.docx" ref={fileRef} />
          <Button onClick={onPreview} disabled={previewing}>
            {previewing ? 'Extracting…' : 'Extract & preview'}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}

        {preview && (
          <div className="space-y-3 border-t pt-3">
            <div className="text-sm text-muted-foreground">
              {preview.filename} · {preview.charCount} chars
              {preview.looksEmpty && (
                <span className="ml-2 text-destructive">⚠ extracted text is suspiciously short — likely a scanned PDF</span>
              )}
            </div>
            <Textarea rows={8} value={preview.extractedText} readOnly />
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Document name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>Kind</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as DocumentKind)}
                >
                  <option value="brand_voice">Brand voice</option>
                  <option value="knowledge_base">Knowledge base</option>
                  <option value="example_thread">Example thread</option>
                </select>
              </div>
              {(kind === 'knowledge_base' || kind === 'example_thread') && (
                <div>
                  <Label>Topic {kind === 'example_thread' ? '(required)' : '(optional)'}</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={topicId}
                    onChange={(e) => setTopicId(e.target.value)}
                  >
                    <option value="">No topic</option>
                    {topics.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {kind === 'knowledge_base' && (
                <label className="flex items-center gap-2 text-sm self-end">
                  <input
                    type="checkbox"
                    checked={alwaysInclude}
                    onChange={(e) => setAlwaysInclude(e.target.checked)}
                  />
                  Always include in prompts
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreview(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => confirm.mutate()}
                disabled={!name || confirm.isPending}
              >
                {confirm.isPending ? 'Saving…' : 'Save document'}
              </Button>
            </div>
            {confirm.error && (
              <p className="text-sm text-destructive">{(confirm.error as Error).message}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PastedTextCreate({ topics, onCreated }: { topics: Topic[]; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<DocumentKind>('brand_voice');
  const [topicId, setTopicId] = useState('');
  const [content, setContent] = useState('');
  const [alwaysInclude, setAlwaysInclude] = useState(true);

  const create = useMutation({
    mutationFn: () =>
      api<Document>('/api/v1/documents', {
        method: 'POST',
        body: {
          name,
          kind,
          topicId: topicId || null,
          content,
          alwaysInclude,
        },
      }),
    onSuccess: () => {
      setName('');
      setContent('');
      onCreated();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paste text</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label>Kind</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value as DocumentKind)}
            >
              <option value="brand_voice">Brand voice</option>
              <option value="knowledge_base">Knowledge base</option>
              <option value="example_thread">Example thread</option>
            </select>
          </div>
          {(kind === 'knowledge_base' || kind === 'example_thread') && (
            <div>
              <Label>Topic</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
              >
                <option value="">No topic</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {kind !== 'example_thread' && (
            <label className="flex items-center gap-2 text-sm self-end">
              <input
                type="checkbox"
                checked={alwaysInclude}
                onChange={(e) => setAlwaysInclude(e.target.checked)}
              />
              Always include in prompts
            </label>
          )}
        </div>
        <div>
          <Label>Content</Label>
          <Textarea rows={6} value={content} onChange={(e) => setContent(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <Button onClick={() => create.mutate()} disabled={!name || !content || create.isPending}>
            {create.isPending ? 'Saving…' : 'Save document'}
          </Button>
        </div>
        {create.error && <p className="text-sm text-destructive">{(create.error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}
